import type React from "react";

import type { ColumnDef } from "@blis/insights/entities/types";
import type { ExploreFilterState } from "@blis/insights/explore/explore-filter-state";
import { defaultExploreFilterState } from "@blis/insights/explore/explore-filter-state";

import { Button } from "@/components/ui/button";
import { Tag, TagGroup, TagList } from "@/components/ui/tag-group";
import {
  buildExploreFilterChips,
  hasActiveExploreFilters,
  resetExploreDateToDefault,
} from "../explore/describeExploreFilters";

export function ExploreFilterBar({
  state,
  columns,
  defaultDateField,
  onChange,
}: {
  state: ExploreFilterState;
  columns: readonly ColumnDef[];
  defaultDateField: string;
  onChange: (next: ExploreFilterState) => void;
}) {
  if (!hasActiveExploreFilters(state)) {
    return null;
  }

  const chips = buildExploreFilterChips(state, columns, defaultDateField, {
    onDateReset: () => onChange(resetExploreDateToDefault(state)),
    onIncludeCancelledChange: (includeCancelled) =>
      onChange({ ...state, includeCancelled }),
    onRemoveColumnFilter: (field) =>
      onChange({
        ...state,
        columnFilters: state.columnFilters.filter((f) => f.field !== field),
      }),
    onClearAll: () => onChange(defaultExploreFilterState()),
  });

  const onRemove = (keys: Set<React.Key>) => {
    for (const key of keys) {
      chips.find((chip) => chip.id === key)?.onRemove?.();
    }
  };

  return (
    <div className="flex shrink-0 items-center justify-between gap-3 border-b bg-muted/10 px-4 py-2">
      <TagGroup
        aria-label="Explore filters"
        color="primary"
        variant="outline"
        onRemove={onRemove}
      >
        <TagList className="flex flex-wrap items-center gap-2">
          {chips.map((chip) => (
            <Tag key={chip.id} id={chip.id}>
              {chip.label}
            </Tag>
          ))}
        </TagList>
      </TagGroup>
      <Button
        size="sm"
        variant="ghost"
        onPress={() => onChange(defaultExploreFilterState())}
      >
        Clear filters
      </Button>
    </div>
  );
}
